import i18n from 'i18next';
import {initReactI18next} from 'react-i18next';

// Eagerly load only the fallback (hu) + most common (en) locales at boot. The
// other four are lazy-required on first use (see localeLoaders / ensureBundle
// below). Because Metro runs with `inlineRequires: true`, these require() calls
// are NOT evaluated until invoked, so their ~120 KB-each JSON.parse is skipped
// on cold start — only paid when a user actually switches to that language.
import hu from './locales/hu.json'; // Magyar (fallback / POS default)
import en from './locales/en.json'; // English
// Mail-client keys live in a dedicated bundle (locales/mail/<lng>.json) merged
// into the single `translation` namespace, so the large core locale files stay
// untouched. hu/en are eager (mailbox is reachable from every app mode at boot).
import mailHu from './locales/mail/hu.json';
import mailEn from './locales/mail/en.json';
// VoIP-softphone keys live in a dedicated bundle (locales/voip/<lng>.json) merged
// into the single `translation` namespace, mirroring the mail bundle above.
import voipHu from './locales/voip/hu.json';
import voipEn from './locales/voip/en.json';
// Sales-module keys live in a dedicated bundle (locales/sales/<lng>.json) merged
// into the single `translation` namespace, mirroring the mail/voip bundles above.
// NOTE: these keys are stored FLAT and dotted (e.g. "sales.builder.title"), not
// nested under a `sales` object — i18next's deepFind resolves an exact flat key
// before it walks the keySeparator path, and the core locales already ship a
// plain `sales` string ("Értékesítés") that a nested object would shadow,
// breaking `t('sales')` in HomeScreen/SaleScreen.
import salesHu from './locales/sales/hu.json';
import salesEn from './locales/sales/en.json';
// Apollo.io lead-prospecting keys live in a dedicated bundle
// (locales/apollo/<lng>.json) merged into the single `translation` namespace,
// mirroring the mail/voip/sales bundles above. Same FLAT dotted-key rule as
// sales: the exact key ("apollo.results.total") is looked up before i18next
// walks the keySeparator path, so a nested `apollo` object would shadow the
// screens' own `apollo.*` lookups and is deliberately avoided.
import apolloHu from './locales/apollo/hu.json';
import apolloEn from './locales/apollo/en.json';
// AI-assistant chat keys live in a dedicated bundle (locales/assistant/<lng>.json)
// merged into the single `translation` namespace, mirroring the bundles above.
// Keys are FLAT underscore-separated (assistant_*), so no nesting/shadowing risk.
import assistantHu from './locales/assistant/hu.json';
import assistantEn from './locales/assistant/en.json';
// Procurement (Beszerzés) keys live in a dedicated bundle
// (locales/procurement/<lng>.json) merged into the single `translation`
// namespace, mirroring the bundles above. Same FLAT underscore-separated key
// rule (procurement_*), so no nesting/shadowing risk. hu/en are eager because
// the module is reachable from the POS "Funkciók" screen at boot, not only
// from the Management hub.
import procurementHu from './locales/procurement/hu.json';
import procurementEn from './locales/procurement/en.json';
import driverHu from './locales/driver/hu.json';
import driverEn from './locales/driver/en.json';
// Utility bills & meters (Rezsi nyilvántartás) keys live in a dedicated bundle
// (locales/utilities/<lng>.json) merged into the single `translation` namespace.
// FLAT utility_* keys (plus the hub tile / tab labels). hu/en are eager because the
// module is reachable from the Modules hub at boot.
import utilitiesHu from './locales/utilities/hu.json';
import utilitiesEn from './locales/utilities/en.json';
// Fleet (Flotta) management screens: FLAT fleet_* keys + hub tile / tab labels
// (locales/fleet/<lng>.json), merged into the single `translation` namespace.
import fleetHu from './locales/fleet/hu.json';
import fleetEn from './locales/fleet/en.json';
// Happy AI (Jev suggestions/insights) keys live in a dedicated bundle
// (locales/ai/<lng>.json) merged into the single `translation` namespace,
// mirroring the bundles above. Same FLAT dotted-key rule as sales/apollo
// (e.g. "ai.heat_hot") so a nested `ai` object never shadows these lookups.
// hu/en are eager because the module is reachable from the CRM hub and the
// management dashboard at boot.
import aiHu from './locales/ai/hu.json';
import aiEn from './locales/ai/en.json';
// Workday (Munkanap) clock-in/out keys: FLAT workday_* keys (plus myday_kicker) in locales/workday/<lng>.json,
// merged into the single `translation` namespace. hu/en eager (the pill is on every module's home screen).
import workdayHu from './locales/workday/hu.json';
import workdayEn from './locales/workday/en.json';
// Insight widgets (finance / bank / sales / operations / custom home-screen widgets): FLAT wi_* keys in
// locales/insights/<lng>.json, merged into the single `translation` namespace. hu/en eager (the Android widget
// configuration activity and the snapshot builder resolve labels from a cold JS context).
import insightsHu from './locales/insights/hu.json';
import insightsEn from './locales/insights/en.json';
// "Share to Happy" (OS share-sheet target): FLAT share_* keys in locales/share/<lng>.json, merged into the
// single `translation` namespace. hu/en eager (a share can open the app cold, straight onto ShareToHappyScreen).
import shareHu from './locales/share/hu.json';
import shareEn from './locales/share/en.json';

export const selectableLanguages = ["en", "hu", "de", "cn", "ro", "svn"]

/**
 * Lazy loaders for every supported locale. hu/en resolve to the already-parsed
 * eager imports; the rest are deferred require()s evaluated on demand.
 * @type {Record<string, () => object>}
 */
const localeLoaders = {
    hu: () => ({...hu, ...mailHu, ...voipHu, ...salesHu, ...apolloHu, ...assistantHu, ...procurementHu, ...driverHu, ...utilitiesHu, ...fleetHu, ...aiHu, ...workdayHu, ...insightsHu, ...shareHu}),
    en: () => ({...en, ...mailEn, ...voipEn, ...salesEn, ...apolloEn, ...assistantEn, ...procurementEn, ...driverEn, ...utilitiesEn, ...fleetEn, ...aiEn, ...workdayEn, ...insightsEn, ...shareEn}),
    de: () => ({...require('./locales/de.json'), ...require('./locales/mail/de.json'), ...require('./locales/voip/de.json'), ...require('./locales/sales/de.json'), ...require('./locales/apollo/de.json'), ...require('./locales/assistant/de.json'), ...require('./locales/procurement/de.json'), ...require('./locales/driver/de.json'), ...require('./locales/utilities/de.json'), ...require('./locales/fleet/de.json'), ...require('./locales/ai/de.json'), ...require('./locales/workday/de.json'), ...require('./locales/insights/de.json'), ...require('./locales/share/de.json')}),   // German
    cn: () => ({...require('./locales/cn.json'), ...require('./locales/mail/cn.json'), ...require('./locales/voip/cn.json'), ...require('./locales/sales/cn.json'), ...require('./locales/apollo/cn.json'), ...require('./locales/assistant/cn.json'), ...require('./locales/procurement/cn.json'), ...require('./locales/driver/cn.json'), ...require('./locales/utilities/cn.json'), ...require('./locales/fleet/cn.json'), ...require('./locales/ai/cn.json'), ...require('./locales/workday/cn.json'), ...require('./locales/insights/cn.json'), ...require('./locales/share/cn.json')}),   // Chinese (Mandarin)
    ro: () => ({...require('./locales/ro.json'), ...require('./locales/mail/ro.json'), ...require('./locales/voip/ro.json'), ...require('./locales/sales/ro.json'), ...require('./locales/apollo/ro.json'), ...require('./locales/assistant/ro.json'), ...require('./locales/procurement/ro.json'), ...require('./locales/driver/ro.json'), ...require('./locales/utilities/ro.json'), ...require('./locales/fleet/ro.json'), ...require('./locales/ai/ro.json'), ...require('./locales/workday/ro.json'), ...require('./locales/insights/ro.json'), ...require('./locales/share/ro.json')}),   // Romanian
    svn: () => ({...require('./locales/svn.json'), ...require('./locales/mail/svn.json'), ...require('./locales/voip/svn.json'), ...require('./locales/sales/svn.json'), ...require('./locales/apollo/svn.json'), ...require('./locales/assistant/svn.json'), ...require('./locales/procurement/svn.json'), ...require('./locales/driver/svn.json'), ...require('./locales/utilities/svn.json'), ...require('./locales/fleet/svn.json'), ...require('./locales/ai/svn.json'), ...require('./locales/workday/svn.json'), ...require('./locales/insights/svn.json'), ...require('./locales/share/svn.json')}), // Slovenian
};

/**
 * Ensure a language's translation bundle is registered with i18next before it
 * becomes active. Idempotent and synchronous, so switching to a not-yet-loaded
 * locale never briefly renders raw translation keys.
 * @param {string} lng Language code (e.g. 'de').
 * @returns {void}
 */
const ensureBundle = (lng) => {
    if (lng && localeLoaders[lng] && !i18n.hasResourceBundle(lng, 'translation')) {
        i18n.addResourceBundle(lng, 'translation', localeLoaders[lng](), true, true);
    }
};

i18n
    .use(initReactI18next)
    .init({
        compatibilityJSON: 'v4',  // Ensure compatibility with React Native
        resources: {
            // Only the fallback + English are bundled up-front; the remaining
            // locales are injected on demand via ensureBundle().
            hu: { translation: {...hu, ...mailHu, ...voipHu, ...salesHu, ...apolloHu, ...assistantHu, ...procurementHu, ...driverHu, ...utilitiesHu, ...fleetHu, ...aiHu, ...workdayHu, ...insightsHu, ...shareHu} },
            en: { translation: {...en, ...mailEn, ...voipEn, ...salesEn, ...apolloEn, ...assistantEn, ...procurementEn, ...driverEn, ...utilitiesEn, ...fleetEn, ...aiEn, ...workdayEn, ...insightsEn, ...shareEn} },
        },
        cache: true,
        debug: __DEV__,  // Only debug in development
        partialBundledLanguages: true, // we add the remaining bundles at runtime
        supportedLngs: ["hu", "en", "de", "cn", "ro", "svn"],
        saveMissingTo: 'all',
        updateMissing: true,
        // NOTE: `lng` is intentionally NOT set here. The initial language is
        // restored from persistent storage on boot in App.jsx via
        // i18n.changeLanguage(savedLanguage) once AsyncStorage/SecureStore
        // have been read. Hard-coding `lng` caused users to briefly see the
        // wrong language on cold start and, in rare async-read failure
        // scenarios, to permanently "forget" their selected language.
        fallbackLng: 'hu', // Fallback language (Hungarian POS default)
        interpolation: {
            escapeValue: true, // React already handles escaping
        },
        missingKeyHandler: (lng, ns, key) => {
            if (__DEV__) {
                console.warn(`📄 Hiányzó nyelvi fordítás: ${key} a következő nyelvi file-ból: ${lng}❗️️`);
            }
        },
    });

// Wrap changeLanguage so EVERY call site (App.jsx boot restore, the in-app
// language picker, etc.) transparently loads the target bundle (and the
// fallback) first. This keeps lazy-loading correct without touching callers.
const originalChangeLanguage = i18n.changeLanguage.bind(i18n);
i18n.changeLanguage = (lng, ...rest) => {
    ensureBundle(lng);
    ensureBundle('hu'); // fallback must always be resolvable
    return originalChangeLanguage(lng, ...rest);
};

export default i18n;
